import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Label, RadioGroup, SectionTitle } from './FormComponents';

import type { Eligibility } from '../../types/application';

export type StepBErrors = Partial<Record<keyof Eligibility, string>>;

interface StepBEligibilityProps {
  value: Eligibility;
  errors: StepBErrors;
  onChange: (patch: Partial<Eligibility>) => void;
}

export function StepBEligibility({ value, errors, onChange }: StepBEligibilityProps) {
  const { t } = useTranslation();

  return (
    <div className='space-y-6'>
      <SectionTitle title={t('wizard.stepB.title')} subtitle={t('wizard.stepB.subtitle')} />

      {/* Age Confirmation */}
      <div
        className={`p-4 bg-gray-50 rounded-md border ${errors.isOver18 ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'}`}>
        <Checkbox
          label={t('wizard.stepB.ageConfirmation')}
          checked={value.isOver18}
          onChange={(checked) => onChange({ isOver18: checked })}
          error={errors.isOver18}
        />
        {errors.isOver18 ? <p className='text-xs text-red-600 mt-2'>{errors.isOver18}</p> : null}
      </div>

      {/* Work Authorization */}
      <div>
        <Label required error={errors.isAuthorizedToWork}>
          {t('wizard.stepB.workAuthorization')}
        </Label>
        <RadioGroup
          name='isAuthorizedToWork'
          value={value.isAuthorizedToWork}
          options={[
            { value: true, label: t('common.yes') },
            { value: false, label: t('common.no') },
          ]}
          onChange={(val) => onChange({ isAuthorizedToWork: val as boolean })}
          error={errors.isAuthorizedToWork}
          inline
        />
      </div>

      {/* Background Check Consent Placeholder */}
      <div className='p-4 bg-blue-50 rounded-md border border-blue-200'>
        <div className='flex items-start gap-2'>
          <Info size={16} className='text-blue-600 mt-0.5 flex-shrink-0' />
          <div>
            <p className='text-sm font-medium text-blue-800'>{t('wizard.stepB.bgCheckTitle')}</p>
            <p className='text-xs text-blue-600 mt-1'>{t('wizard.stepB.bgCheckDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
